import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getSecureRandomInt } from "../../utils/cryptoUtils";
import { resolveHospitalName } from "../../utils/printEngine";
import "./PrintInvoice.css";
import "./print.css";

const PrintInvoice = () => {
  const { user } = useAuth();
  const hospitalName = resolveHospitalName(user?.hospital_name);
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    const data =
      location.state?.invoiceData ||
      JSON.parse(sessionStorage.getItem("invoiceData") || "{}");
    let isMounted = true;

    if (data && Object.keys(data).length > 0) {
      setInvoiceData(data);
    } else {
      navigate("/patients");
    }

    const handleAfterPrint = () => {
      if (isMounted) {
        sessionStorage.removeItem("invoiceData");
        localStorage.removeItem("invoiceData");
        navigate("/patients");
      }
    };

    window.addEventListener("afterprint", handleAfterPrint);

    if (data && Object.keys(data).length > 0) {
      // Delay print to allow component to render
      const printTimeout = setTimeout(() => window.print(), 500);

      return () => {
        isMounted = false;
        clearTimeout(printTimeout);
        window.removeEventListener("afterprint", handleAfterPrint);
      };
    }

    return () => {
      isMounted = false;
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [location.state, navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    if (num === 0) return "Zero";

    let words = "";
    let crores = Math.floor(num / 10000000);
    let lakhs = Math.floor((num % 10000000) / 100000);
    let thousands_val = Math.floor((num % 100000) / 1000);
    let hundreds = Math.floor((num % 1000) / 100);
    let remainder = num % 100;

    if (crores > 0) {
      words += convertTwoDigit(crores) + " Crore ";
    }
    if (lakhs > 0) {
      words += convertTwoDigit(lakhs) + " Lakh ";
    }
    if (thousands_val > 0) {
      words += convertTwoDigit(thousands_val) + " Thousand ";
    }
    if (hundreds > 0) {
      words += ones[hundreds] + " Hundred ";
    }
    if (remainder > 0) {
      words += convertTwoDigit(remainder);
    }

    function convertTwoDigit(num) {
      if (num < 10) return ones[num];
      if (num >= 11 && num <= 19) return teens[num - 11];
      return (
        tens[Math.floor(num / 10)] + (num % 10 > 0 ? " " + ones[num % 10] : "")
      );
    }

    return words.trim() + " Rupees Only";
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (!invoiceData) {
    return (
      <div className="print-invoice-page">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            background: "white",
            borderRadius: "12px",
            padding: "40px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #2c5282",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              marginBottom: "20px",
            }}
          ></div>
          <h3 style={{ color: "#2c5282", margin: "0 0 10px 0" }}>
            Generating Invoice...
          </h3>
          <p style={{ color: "#666", margin: 0 }}>
            Please wait while we prepare your invoice for printing.
          </p>
        </div>
      </div>
    );
  }

  const additionalTotal = Array.isArray(invoiceData.additionalServices)
    ? invoiceData.additionalServices.reduce(
        (sum, svc) => sum + (parseFloat(svc.amount) || 0),
        0,
      )
    : 0;
  const totalAmount = parseFloat(invoiceData.consultationFee || 0) + additionalTotal;

  return (
    <div className="print-invoice-page">
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          background: "#1e293b",
          color: "white",
          padding: "8px 18px",
          borderRadius: "6px",
          zIndex: 1000,
          fontSize: "13px",
          fontWeight: "600",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
        className="printing-banner"
      >
        <span
          style={{
            display: "inline-block",
            width: "12px",
            height: "12px",
            border: "2px solid #94a3b8",
            borderTopColor: "#38bdf8",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span>Preparing Invoice for Print...</span>
      </div>
      <div ref={printRef} className="invoice-container">
        <div className="invoice-header">
          <div className="hospital-logo" style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <div className="hospital-emblem-icon" style={{ marginTop: "2px" }}>
              <svg width="42" height="42" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="44" height="44" rx="10" fill="#2c5282" />
                <rect x="18" y="10" width="8" height="24" rx="2" fill="#ffffff" />
                <rect x="10" y="18" width="24" height="8" rx="2" fill="#ffffff" />
                <circle cx="22" cy="22" r="18" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.35" fill="none" />
              </svg>
            </div>
            <div className="hospital-info">
              <h2 style={{ margin: 0, textTransform: "uppercase", letterSpacing: "-0.2px" }}>{hospitalName}</h2>
              <p style={{ margin: "2px 0 0 0", color: "#2c5282", fontWeight: 700, fontSize: "12px" }}>
                Multi-Speciality Hospital &amp; Research Centre
              </p>
              <p style={{ margin: "2px 0 0 0", color: "#475569", fontSize: "11px" }}>
                NABH Accredited • 24x7 Emergency: 108 • Ph: +91 80 4920 0000 • Web: pran.ai
              </p>
              <p style={{ margin: "2px 0 0 0", color: "#64748b", fontSize: "11px" }}>
                Outpatient Billing &amp; Revenue Department • Official Receipt
              </p>
            </div>
          </div>
        </div>

        <div className="invoice-title">
          <h1>OUTPATIENT BILL RECEIPT</h1>
        </div>

        <div className="invoice-details">
          <div className="patient-details">
            <h3>PATIENT DETAILS</h3>
            <div className="detail-row">
              <span className="detail-label">Patient Name:</span>
              <span className="detail-value">
                {invoiceData.fullName || "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Age:</span>
              <span className="detail-value">{invoiceData.age || "N/A"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">DOB:</span>
              <span className="detail-value">
                {invoiceData.dob
                  ? invoiceData.dob.split("T")[0]
                  : invoiceData.dateOfBirth
                    ? invoiceData.dateOfBirth.split("T")[0]
                    : "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Gender:</span>
              <span className="detail-value">
                {invoiceData.gender || "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Mobile:</span>
              <span className="detail-value">
                {invoiceData.mobileNumber || "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">UHID:</span>
              <span className="detail-value">{invoiceData.uhid || "N/A"}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">ABHA Number:</span>
              <span className="detail-value">
                {invoiceData.abhaNumber || "N/A"}
                {(invoiceData.abhaAddress || invoiceData.abhaaddress) && (
                  <span
                    style={{
                      marginLeft: "8px",
                      color: "#666",
                      fontSize: "0.9em",
                    }}
                  >
                    · {invoiceData.abhaAddress || invoiceData.abhaaddress}
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="invoice-info">
            <h3>BILL DETAILS</h3>
            <div className="detail-row">
              <span className="detail-label">Bill No:</span>
              <span className="detail-value">
                {invoiceData.billNumber || `OPB${Date.now()}`}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">IP No:</span>
              <span className="detail-value">
                {invoiceData.ipNumber || getSecureRandomInt(100000, 999999)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Bill Date:</span>
              <span className="detail-value">{getCurrentDate()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Bill Time:</span>
              <span className="detail-value">{getCurrentTime()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Admission Date:</span>
              <span className="detail-value">{getCurrentDate()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Admission Time:</span>
              <span className="detail-value">{getCurrentTime()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Doctor:</span>
              <span className="detail-value">
                {invoiceData.doctorName || "N/A"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Department:</span>
              <span className="detail-value">
                {invoiceData.departmentName || "N/A"}
              </span>
            </div>
          </div>
        </div>

        <h3>DETAILS</h3>
        <table className="services-table">
          <thead>
            <tr>
              <th>Service Name</th>
              <th className="amount-column">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Consultation Fee</td>
              <td className="amount-column">
                {formatCurrency(invoiceData.consultationFee || 0)}
              </td>
            </tr>
            {invoiceData.additionalServices &&
              invoiceData.additionalServices.map((service, index) => (
                <tr key={index}>
                  <td>{service.name}</td>
                  <td className="amount-column">
                    {formatCurrency(service.amount)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="total-section">
          <div className="total-row">
            <span>Subtotal:</span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>
          <div className="total-row">
            <span>Tax (if applicable):</span>
            <span>₹0.00</span>
          </div>
          <div className="total-row final">
            <span>Bill Amount:</span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>

          <div className="amount-words">
            <strong>In Words:</strong>
            <br />
            {numberToWords(Math.floor(totalAmount))}
          </div>
        </div>

        <div className="footer-info">
          <p>
            This is a computer-generated tax invoice and billing receipt issued under hospital electronic health record systems.
          </p>
          <p>
            Healthcare services provided by registered clinical establishments are exempt from GST under applicable notifications.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginTop: "36px",
              paddingTop: "16px",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <div style={{ textAlign: "center", width: "180px" }}>
              <div
                style={{
                  borderBottom: "1px solid #94a3b8",
                  height: "30px",
                  marginBottom: "5px",
                }}
              ></div>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#334155",
                }}
              >
                Patient / Attendant Signature
              </span>
            </div>
            <div style={{ textAlign: "center", width: "180px" }}>
              <div
                style={{
                  borderBottom: "1px solid #94a3b8",
                  height: "30px",
                  marginBottom: "5px",
                }}
              ></div>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "#334155",
                }}
              >
                Authorized Cashier / Accounts
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintInvoice;
